import styled from 'styled-components';
import { useMemo, useState } from 'react';
import {
  EntityLatestRiskScore,
  RiskModelSummary,
} from 'app/modules/riskRatings/responses';
import {
  U21Chip,
  U21MenuLayout,
  U21Spacer,
  U21Skeleton,
} from 'app/shared/u21-ui/components';
import { useFetchEntityRiskScore } from 'app/modules/riskRatings/queries/useFetchEntityRiskScore';
import { RiskScoreDetails } from 'app/modules/riskRatings/components/RiskScoreDetails';
import { IconAlertTriangle } from '@u21/tabler-icons';
import { getRiskCategoryStyles } from 'app/modules/riskRatings/helpers';

interface OwnProps {
  entityExternalId: string;
}

interface EntityRiskScoreDisplayOwnProps {
  disablePopover?: boolean;
  latestRiskScore: EntityLatestRiskScore;
  riskModelSummary: Pick<
    RiskModelSummary,
    'high_risk_floor' | 'id' | 'low_risk_ceiling' | 'title'
  >;
}

export const EntityRiskScoreDisplay = (
  props: EntityRiskScoreDisplayOwnProps,
) => {
  const { disablePopover, latestRiskScore, riskModelSummary } = props;

  const [evidenceOpen, setEvidenceOpen] = useState<boolean>(false);

  const riskStyles = useMemo(
    () =>
      getRiskCategoryStyles(
        latestRiskScore.score,
        riskModelSummary.high_risk_floor,
        riskModelSummary.low_risk_ceiling,
      ),
    [riskModelSummary, latestRiskScore.score],
  );

  const ScoreComponent = (
    <U21Chip
      tooltip={disablePopover ? undefined : 'Click to see more details'}
      variant="ghost"
      color={riskStyles.colorSchema}
      icon={<IconAlertTriangle />}
      onClick={
        disablePopover
          ? undefined
          : (e) => {
              e.stopPropagation();
              setEvidenceOpen(true);
            }
      }
    >
      {latestRiskScore.source === 'MANUAL' ? (
        <>
          {riskStyles.label}:&nbsp;
          <s>{Math.round(latestRiskScore.override.model_score)}</s>&nbsp;→&nbsp;
          <b>{Math.round(latestRiskScore.score)}</b>
        </>
      ) : (
        <>
          {riskStyles.label}: {Math.round(latestRiskScore.score)}
        </>
      )}
    </U21Chip>
  );

  if (disablePopover) {
    return ScoreComponent;
  }

  return (
    <U21Spacer>
      <U21MenuLayout
        open={evidenceOpen}
        onClose={(e) => {
          e.stopPropagation();
          setEvidenceOpen(false);
        }}
        trigger={ScoreComponent}
      >
        <PopoverContainer
          onClick={(e) => {
            // Stop React's synthetic bubbling (prevents parent row onClick)
            // without touching the native event, so portaled MUI click-away
            // listeners (datetime picker, autocomplete, etc.) still fire.
            // eslint-disable-next-line no-param-reassign
            e.isPropagationStopped = () => true;
          }}
        >
          <RiskScoreDetails
            latestRiskScore={latestRiskScore}
            riskModelSummary={riskModelSummary}
          />
        </PopoverContainer>
      </U21MenuLayout>
    </U21Spacer>
  );
};

// Wraps the logic for retrieving the risk score for an entity and showing the right loader
export const EntityRiskScore = ({ entityExternalId }: OwnProps) => {
  const { data: riskScoreRes, isLoading } =
    useFetchEntityRiskScore(entityExternalId);
  if (isLoading) {
    return <U21Skeleton width={80} />;
  } else if (riskScoreRes && riskScoreRes.riskModel) {
    // We have an active model, either show the score or that the entity is unscored.
    if (riskScoreRes.result) {
      return (
        <EntityRiskScoreDisplay
          riskModelSummary={riskScoreRes.riskModel}
          latestRiskScore={riskScoreRes.result}
        />
      );
    }
    // There's no score, it should be computed _soon_
    return (
      <U21Chip
        tooltip="This entity has not been scored yet. It may be new and will be scored within 24 hours, or it may be excluded by the filters on the active risk model."
        variant="ghost"
      >
        Unscored
      </U21Chip>
    );
  }

  // Not Loading and no results
  return null;
};

const PopoverContainer = styled.div`
  padding: ${({ theme }) => theme.spacing(2)};
  width: 542px;
`;
